/**
 * Slugs of the guide pages served by BookStack (sections configured backend-side in
 * BookStackConfig::DEFAULT_SOURCE_URLS) and the map that binds them to the routes of the
 * application.
 *
 * Adding a screen to the documentation means adding an entry here - the pages themselves know
 * nothing about the guide.
 */
export const GUIDE_PAGE_ORDERS = 'dodawanie-zlecen';
export const GUIDE_PAGE_SETTINGS = 'ustawienia';
export const GUIDE_PAGE_PLANNING = 'planowanie-zadan-i-wyslanie-ich-do-kierowcow';
export const GUIDE_PAGE_DASHBOARD = 'analiza-realizowanych-zlecen';
export const GUIDE_PAGE_TACTICAL_PLAN = 'podstawowa-konfiguracja#bkmrk-tworzenie-planu-takt';
export const GUIDE_PAGE_MICRO_GRID = 'przewodnik-po-mikro-komorkach-6kR';

/**
 * Route patterns are written exactly as in mainMenu.items[].to, ':id' included.
 *
 * The map depends on whether the customer has the micro-grid feature: on the tactical-plan
 * editor the micro-grid guide is only relevant when settings_readonly.micro_grid is on, otherwise
 * the generic tactical-plan page is shown.
 */
export function guidePageByRoute(microGridEnabled: boolean): Record<string, string> {
    return {
        '/': GUIDE_PAGE_ORDERS,
        '/upload-file': GUIDE_PAGE_ORDERS,
        '/settings': GUIDE_PAGE_SETTINGS,
        '/vehicles': 'podstawowa-konfiguracja#bkmrk-tworzenie-pojazdu',
        '/tactical-plan': GUIDE_PAGE_TACTICAL_PLAN,
        '/warehouses': 'podstawowa-konfiguracja#bkmrk-tworzenie-magazynu',
        '/planning': GUIDE_PAGE_PLANNING,
        '/planning/:id': GUIDE_PAGE_PLANNING,
        '/tactical-plan/:id': microGridEnabled ? GUIDE_PAGE_MICRO_GRID : GUIDE_PAGE_TACTICAL_PLAN,
        '/dashboard': GUIDE_PAGE_DASHBOARD,
    };
}

function toSegments(path: string): string[] {
    return path.split('/').filter(segment => segment !== '');
}

function matches(routeSegments: string[], pathSegments: string[]): boolean {
    if (routeSegments.length !== pathSegments.length) {
        return false;
    }

    return routeSegments.every(
        (segment, index) => segment.startsWith(':') || segment === pathSegments[index]
    );
}

/**
 * Guide page of the screen behind the given pathname, '' when the screen has no documentation yet.
 *
 * `microGridEnabled` gates the micro-grid guide on the tactical-plan editor; it defaults to false
 * (fail-closed) so a caller that cannot resolve the customer flag never surfaces the micro-grid
 * page.
 */
export function resolveGuidePage(pathname: string, microGridEnabled = false): string {
    const pathSegments = toSegments(pathname);

    for (const [route, slug] of Object.entries(guidePageByRoute(microGridEnabled))) {
        if (matches(toSegments(route), pathSegments)) {
            return slug;
        }
    }

    return '';
}
